// ============================================================================
// Invoice Audit (3-way match) — 9-step audit workbench + persistent AI panel.
// Sidebar and header are provided by AppShell; this view renders content only.
// ============================================================================
import React, { useState, useEffect, useRef } from "react";

const SYNC = [
  { name: "SAP ERP", sub: "Synced 1 min ago" }, { name: "SAP Ariba", sub: "Synced 2 min ago" },
  { name: "Email Inbox", sub: "Synced 4 min ago" }, { name: "Contract Repository", sub: "Synced 6 min ago" },
  { name: "Vendor Portal", sub: "Synced 3 min ago" }, { name: "Quality Mgmt System", sub: "Synced 5 min ago" },
];

const STEPS = [
  "Vendor & Transaction Setup", "Document Intake & Extraction", "AI Processing Engine",
  "Exception Detection", "Exception Workbench", "Approval Workflow",
  "Payment Readiness", "Audit Trail", "Analytics Dashboard",
];

const VENDOR = "Siemens AG";
const VENDOR_MASTER = [
  ["Vendor ID", "VEND-1001"], ["Supplier Category", "Industrial Automation"], ["Risk Score", "32 — Low", "green"],
  ["Contract Status", "Active"], ["Payment Terms", "Net 45"], ["Preferred Supplier", "Yes", "green"],
  ["Sanctioned Parties Check", "Clear", "green"],
];
const AI_INSIGHTS = [
  ["Total Historical Spend", "$4.82M"], ["Avg Invoice Cycle Time", "6.4 days"], ["Open Invoice Count", "14"],
  ["Previous Audit Exceptions", "7"], ["Supplier Performance", "88/100 (improving)"],
];

const DOCS_REQUIRED = [
  ["Purchase Order", "po.pdf · uploaded · 2 pages"], ["Goods Receipt Note", "grn.pdf · uploaded · 2 pages"], ["Invoice", "inv.pdf · uploaded · 3 pages"],
];
const DOCS_OPTIONAL = [
  ["Contract", "contract.pdf · uploaded · 2 pages"], ["Delivery Note", "deliveryNote.pdf · uploaded · 1 pages"],
  ["Quality Inspection Report", "qiReport.pdf · uploaded · 2 pages"], ["Email Approval Thread", null],
];
const PREFLIGHT = [["Completeness Score:", "86%"], ["Ineligible Fields Flagged:", "0"], ["Missing Required Fields:", "0"]];
const EXTRACTION = [
  ["Vendor Name", "Siemens AG", "inv.pdf", "99%"],
  ["Invoice Number", "INV-9114", "inv.pdf", "98%"],
  ["PO Number", "PO-4521188", "po.pdf", "99%"],
  ["GRN Number", "GRN-771390", "grn.pdf", "97%"],
  ["Total Amount", "$6,901.20", "inv.pdf", "96%"],
];

const MATCH_SUMMARY = [["Total Line Items", "3"], ["Fully Matched", "3", "green"], ["Partially Matched", "0"], ["Failed", "0"]];
const MATCH_ROWS = [
  ["Item A — Industrial Bearing Assembly", "100 @ $50.00", "100", "100 @ $50.00", "Full Match"],
  ["Item B — Mounting Bracket Kit", "40 @ $22.00", "40", "40 @ $22.00", "Full Match"],
  ["Item C — Gasket Set", "60 @ $8.50", "60", "60 @ $8.50", "Full Match"],
];

const WORKFLOW = ["Invoice Received", "AI Auto-Approval", "Payment Release"];
const APPROVAL_ACTIONS = ["Approve", "Request More Info", "Delegate", "Reject"];
const ESCALATION = [
  "Unassigned exceptions escalate to the Category Manager after 24 hours.",
  "Unresolved exceptions escalate to the Finance Controller after 72 hours.",
  "Critical severity exceptions escalate automatically after 4 hours without action.",
  "Fraud & Risk category exceptions notify the Finance Controller immediately on detection.",
];

const HEALTH = [["PO Match", "100%"], ["Receipt Match", "100%"], ["Compliance", "100%"], ["Risk", "97%"]];
const PAYMENT = [
  ["Invoice Number", "INV-9114"], ["Vendor", "Siemens AG"], ["Total Amount", "$6,901.20"],
  ["Payment Method", "Bank Transfer (ACH)"], ["Bank Reference", "BR-88304"], ["Due Date", "Aug 18, 2026"],
];

const AUDIT_BASE = [
  { who: "You", title: "Transaction set loaded", detail: "Set B — Clean Scenario loaded for Siemens AG — PO-4521188 / GRN-771390 / INV-9114", time: "07:23 pm" },
  { who: "AI Engine", title: "3-way match & validation complete", detail: "3 line items processed, 0 exceptions raised.", time: "07:25 pm", link: "Why did AI do this?" },
];

const ANALYTICS_KPIS = [
  ["Total Invoices Processed", "4,286"], ["Auto-Approved", "71%"], ["Manual Review Rate", "29%"], ["Exception Rate", "18%"],
  ["Avg Processing Time", "6.8 min"], ["Duplicate Payments Prevented", "$186,400"], ["Overcharge Recovery", "$92,150"], ["Early Payment Discounts Captured", "$214,900"],
];
const EXC_TREND: [string, number][] = [["Commercial", 38], ["Operational", 52], ["Compliance", 24], ["Fraud & Risk", 11]];
const FUNNEL: [string, number][] = [["Received", 4286], ["AI Processed", 3043], ["Auto-Approved", 3043], ["Manual Review", 1243]];

const AI_PROMPTS = [
  "Why is invoice INV-9001 blocked?", "Show all unmatched line items for this vendor.",
  "What caused the price variance on line 7?", "Show me previous invoices from Siemens AG.",
  "What is the supplier's risk profile?", "Has this invoice been submitted before?",
];
const AI_INTRO = "Ask me about any vendor, or any exception on screen — for example, “Why is invoice INV-9001 blocked?”";

// ============================== ICONS =======================================
const svg = (p: any) => ({ viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", ...p });
const Ic = {
  sparkles: (p: any) => (<svg {...svg(p)}><path d="M12 3l1.8 4.9L18.7 9.7 13.8 11.5 12 16.4 10.2 11.5 5.3 9.7 10.2 7.9 12 3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M19 14l.7 1.9 1.9.7-1.9.7L19 19.2 18.3 17.3 16.4 16.6l1.9-.7L19 14Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>),
  chevron: (p: any) => (<svg {...svg(p)}><path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  chevronRight: (p: any) => (<svg {...svg(p)}><path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  circleCheck: (p: any) => (<svg {...svg(p)}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/><path d="m8.5 12 2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  circleX: (p: any) => (<svg {...svg(p)}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/><path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  send: (p: any) => (<svg {...svg(p)}><path d="M4 12 20 4l-6 16-2.5-6.5L4 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>),
  upload: (p: any) => (<svg {...svg(p)}><path d="M12 16V6m0 0-4 4m4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  download: (p: any) => (<svg {...svg(p)}><path d="M12 4v10m0 0-4-4m4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  file: (p: any) => (<svg {...svg(p)}><path d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M13 3v5h5M8 13h8M8 17h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
};

// ============================== STATUS BAR + SYNC ===========================
function StatusBar({ vendor, txnSet }: { vendor: string; txnSet: string }) {
  return (
    <div className="ia-statusbar">
      <div className="ia-statusbar__info">
        <div className="ia-field">
          <span className="ia-field__label ia-field__label--plain">Active Vendor:</span>
          <span className="ia-field__value ia-field__value--md">{vendor || "None selected"}</span>
        </div>
        <div className="ia-field">
          <span className="ia-field__label ia-field__label--plain">Transaction Set:</span>
          <span className="ia-field__value ia-field__value--md">{txnSet || "None loaded"}</span>
        </div>
      </div>
      <div className="ia-statusbar__syncs">
        {SYNC.map((s, i) => (
          <div key={i} className="ia-sync-pill">
            <div className="ia-sync-pill__row">
              <span className="ia-sync-dot" />
              <span className="ia-sync-name">{s.name}</span>
            </div>
            <span className="ia-sync-sub">{s.sub}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================== STEPPER =====================================
function StepsList({ active, onSelect, enabled }: { active: number; onSelect: (i: number) => void; enabled: boolean }) {
  return (
    <div className="ia-steps-list">
      {STEPS.map((label, i) => {
        const on = active === i;
        const canClick = enabled || i === 0;
        return (
          <button key={i} onClick={() => canClick && onSelect(i)} aria-current={on ? "step" : undefined} disabled={!canClick}
            className={`ia-step-btn${on ? " ia-step-btn--active" : ""}`}>
            <span className="ia-step-btn__index">{i + 1}</span>
            <span className="ia-step-btn__label">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ============================== AI PANEL ====================================
function AIPanel({ onHide, onPrompt, input, setInput }: { onHide: () => void; onPrompt: (q: string) => void; input: string; setInput: (v: string) => void }) {
  return (
    <div className="ia-ai-panel">
      <div className="ia-ai-card">
        <div className="ia-ai-head"><Ic.sparkles width={14} height={14} className="ia-icon-purple" /><span className="ia-ai-title">Ask the platform</span></div>
        <div className="ia-ai-intro">
          <span className="ia-ai-intro__text">{AI_INTRO}</span>
          <span className="ia-ai-intro__time">02:33 pm</span>
        </div>
        <div className="ia-ai-prompts">
          {AI_PROMPTS.map((q, i) => (
            <button key={i} onClick={() => onPrompt(q)} className="ia-ai-prompt">{q}</button>
          ))}
        </div>
        <div className="ia-ai-input-row">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about this inv..." className="ia-ai-input" />
          <button aria-label="Send" className="ia-icon-btn-gradient"><Ic.send width={16} height={16} /></button>
        </div>
      </div>
      <button onClick={onHide} className="ia-btn-outline ia-ai-hide"><Ic.circleX width={14} height={14} />Hide AI Panel</button>
    </div>
  );
}

// ============================== shared ======================================
function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`ia-card ${className}`.trim()}>{children}</div>;
}
function CardTitle({ children }: { children: React.ReactNode }) { return <span className="ia-card-title">{children}</span>; }

// ============================== STEP 1 =======================================
function Step1({ vendor, onSelectVendor, txnSet, onLoadSet }: { vendor: string; onSelectVendor: (v: string) => void; txnSet: string; onLoadSet: (s: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }; document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, []);
  return (
    <div className="ia-stack">
      <Card>
        <CardTitle>Select Vendor</CardTitle>
        <div ref={ref} className="ia-vendor-select">
          <button onClick={() => setOpen((v) => !v)} className="ia-vendor-trigger">
            <span className={`ia-vendor-trigger__text${vendor ? "" : " ia-vendor-trigger__text--placeholder"}`}>{vendor || "Select a vendor..."}</span>
            <Ic.chevron width={14} height={14} className={`ia-vendor-trigger__chevron${open ? " ia-vendor-trigger__chevron--open" : ""}`} />
          </button>
          {open && (
            <div className="ia-vendor-options">
              {[VENDOR, "Robert Bosch GmbH", "ABB Ltd"].map((v) => (
                <button key={v} onClick={() => { onSelectVendor(v); setOpen(false); }} className="ia-opt">{v}</button>
              ))}
            </div>
          )}
        </div>
      </Card>
      {!vendor ? (
        <Card className="ia-card--center">
          <span className="ia-empty-message">Select a vendor to view its master data and load a transaction set.</span>
        </Card>
      ) : (
        <>
          <Card>
            <CardTitle>Vendor Master — {vendor}</CardTitle>
            <div className="ia-mgrid ia-mgrid--2">
              {VENDOR_MASTER.map(([l, v, kind], i) => (
                <div key={i} className="ia-field">
                  <span className="ia-field__label">{l}</span>
                  <span className={`ia-field__value${kind === "green" ? " ia-field__value--positive" : ""}`}>{v}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <CardTitle>AI Insights — Historical Context</CardTitle>
            <div className="ia-mgrid ia-mgrid--3">
              {AI_INSIGHTS.map(([l, v], i) => (
                <div key={i} className="ia-field">
                  <span className="ia-field__label">{l}</span>
                  <span className="ia-field__value ia-field__value--lg">{v}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <CardTitle>Transaction Selector</CardTitle>
            <span className="ia-hint-text">Load a pre-populated demo transaction set. This populates every downstream screen automatically.</span>
            <div className="ia-btn-row">
              {["Load Set A — Exception Scenario", "Load Set B — Clean Scenario"].map((s) => {
                const on = (txnSet === "Exception Scenario Loaded" && s.includes("Set A")) || (txnSet === "Clean Scenario Loaded" && s.includes("Set B"));
                return <button key={s} onClick={() => onLoadSet(s.includes("Set A") ? "Exception Scenario Loaded" : "Clean Scenario Loaded")}
                  className={`ia-txn-btn${on ? " ia-txn-btn--active" : ""}`}>{s}</button>;
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ============================== STEP 2 =======================================
function DocRow({ title, meta }: { title: string; meta: string | null }) {
  return (
    <div className="ia-doc-row">
      <div className="ia-doc-icon"><Ic.file width={14} height={14} className="ia-icon-purple" /></div>
      <div className="ia-doc-info">
        <span className="ia-doc-title">{title}</span>
        {meta ? <span className="ia-doc-meta ia-doc-meta--done">{meta}</span> : <span className="ia-doc-meta">Drag &amp; drop or click</span>}
      </div>
      {meta ? <Ic.circleCheck width={16} height={16} className="ia-icon-green" /> : <Ic.upload width={16} height={16} className="ia-icon-gray" />}
    </div>
  );
}
function Step2() {
  const [tab, setTab] = useState("Header Fields");
  return (
    <div className="ia-stack">
      <Card>
        <CardTitle>Document Intake</CardTitle>
        <span className="ia-section-label">Required documents</span>
        <div className="ia-doc-list">{DOCS_REQUIRED.map(([t, m], i) => <DocRow key={i} title={t} meta={m} />)}</div>
        <span className="ia-section-label">Optional documents</span>
        <div className="ia-doc-list">{DOCS_OPTIONAL.map(([t, m], i) => <DocRow key={i} title={t} meta={m} />)}</div>
      </Card>
      <Card>
        <div className="ia-card-header-row">
          <CardTitle>Document Health — Pre-flight Check</CardTitle>
          <button className="ia-btn-gradient"><Ic.download width={14} height={14} />Pull invoices from Ariba</button>
        </div>
        <div className="ia-mgrid ia-mgrid--3">
          {PREFLIGHT.map(([l, v], i) => (
            <div key={i} className="ia-field">
              <span className="ia-field__label ia-field__label--plain">{l}</span>
              <span className={`ia-field__value ia-field__value--stat${i === 0 ? " ia-field__value--positive" : ""}`}>{v}</span>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <div className="ia-pill-tabs">
          {["Header Fields", "Line Items"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`ia-pill-tab${tab === t ? " ia-pill-tab--active" : ""}`}>{t}</button>
          ))}
        </div>
        <div className="ia-table-wrap">
          <table className="ia-table">
            <thead><tr>{["FIELD", "EXTRACTED VALUE", "SOURCE DOCUMENT", "CONFIDENCE"].map((c) => <th key={c}>{c}</th>)}</tr></thead>
            <tbody>{EXTRACTION.map((r, i) => (
              <tr key={i}>
                <td>{r[0]}</td>
                <td className="ia-table__strong">{r[1]}</td>
                <td>{r[2]}</td>
                <td><span className="ia-table__confidence">{r[3]}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ============================== STEP 3 =======================================
function Step3({ processed, onProcess }: { processed: boolean; onProcess: () => void }) {
  return (
    <div className="ia-stack">
      <Card>
        <div className="ia-card-header-row">
          <CardTitle>Processing Engine</CardTitle>
          <button onClick={onProcess} className="ia-btn-gradient ia-btn-gradient--lg">
            <Ic.sparkles width={16} height={16} />{processed ? "Re-run" : "Start Processing"}
          </button>
        </div>
        {processed && (
          <div className="ia-fade ia-stack">
            <span className="ia-subheading">3-Way Match Results</span>
            <div className="ia-match-summary">
              {MATCH_SUMMARY.map(([l, v, k], i) => (
                <div key={i} className="ia-stat-card">
                  <span className="ia-stat-card__label">{l}</span>
                  <span className={`ia-stat-card__value${k === "green" ? " ia-stat-card__value--positive" : ""}`}>{v}</span>
                </div>
              ))}
            </div>
            <div className="ia-table-wrap">
              <table className="ia-table">
                <thead><tr>{["Item", "PO", "Grn Qty", "Invoice", "Match Status"].map((c) => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>{MATCH_ROWS.map((r, i) => (
                  <tr key={i}>
                    <td className="ia-table__strong">{r[0]}</td>
                    <td>{r[1]}</td>
                    <td>{r[2]}</td>
                    <td>{r[3]}</td>
                    <td><span className="ia-badge ia-badge--green"><Ic.circleCheck width={12} height={12} />{r[4]}</span></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================== STEP 4 / 5 (clean states) ===================
function EmptyState({ title, sub }: { title: string; sub?: string }) {
  return (
    <Card className="ia-card--center">
      <div className="ia-empty-icon"><Ic.circleCheck width={24} height={24} className="ia-icon-green" /></div>
      <span className="ia-empty-title">{title}</span>
      {sub && <span className="ia-empty-sub">{sub}</span>}
    </Card>
  );
}

// ============================== STEP 6 =======================================
function Step6({ onAction, actionsTaken }: { onAction: (a: string) => void; actionsTaken: string[] }) {
  return (
    <div className="ia-stack">
      <Card>
        <CardTitle>Workflow Map</CardTitle>
        <div className="ia-workflow">
          {WORKFLOW.map((w, i) => (
            <React.Fragment key={i}>
              <div className={`ia-workflow-stage${i === 0 ? " ia-workflow-stage--done" : ""}`}>{w}</div>
              {i < WORKFLOW.length - 1 && <Ic.chevronRight width={16} height={16} className="ia-workflow-arrow" />}
            </React.Fragment>
          ))}
        </div>
        <div className="ia-btn-row">
          {APPROVAL_ACTIONS.map((a) => {
            const done = actionsTaken.includes(a);
            const primary = a === "Approve";
            return <button key={a} onClick={() => onAction(a)}
              className={`ia-action-btn${primary ? " ia-action-btn--primary" : done ? " ia-action-btn--done" : ""}`}>{a}{done && !primary ? " ✓" : ""}</button>;
          })}
        </div>
      </Card>
      <div className="ia-mgrid ia-mgrid--2">
        <Card>
          <CardTitle>Completed Stages</CardTitle>
          {actionsTaken.length === 0 ? <span className="ia-empty-message">No stages completed yet.</span> : (
            <div className="ia-completed-list">{actionsTaken.map((a, i) => <span key={i} className="ia-completed-item"><Ic.circleCheck width={14} height={14} className="ia-icon-green" />{a}</span>)}</div>
          )}
        </Card>
        <Card>
          <CardTitle>Escalation Rules</CardTitle>
          <div className="ia-escalation-list">{ESCALATION.map((e, i) => <div key={i} className="ia-escalation-item"><span className="ia-escalation-bullet">•</span><span className="ia-escalation-text">{e}</span></div>)}</div>
        </Card>
      </div>
    </div>
  );
}

// ============================== STEP 7 =======================================
function Ring({ value }: { value: string }) {
  const pct = 99, r = 34, circ = 2 * Math.PI * r;
  return (
    <div className="ia-ring">
      <svg width="90" height="90" viewBox="0 0 90 90">
        <circle cx="45" cy="45" r={r} fill="none" className="ia-ring__track" strokeWidth="8" />
        <circle cx="45" cy="45" r={r} fill="none" className="ia-ring__progress" strokeWidth="8" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)} transform="rotate(-90 45 45)" />
      </svg>
      <div className="ia-ring__value"><span className="ia-ring__value-text">{value}</span></div>
    </div>
  );
}
function Step7({ onRelease, released }: { onRelease: () => void; released: boolean }) {
  return (
    <div className="ia-stack">
      <Card>
        <CardTitle>Invoice Health Score</CardTitle>
        <div className="ia-health-row">
          <Ring value="99/100" />
          <div className="ia-health-bars">
            {HEALTH.map(([l, v], i) => (
              <div key={i} className="ia-health-item">
                <span className="ia-health-item__label">{l}</span>
                <div className="ia-health-track"><div className="ia-health-fill" style={{ width: v }} /></div>
                <span className="ia-health-item__value">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
      <Card>
        <CardTitle>Payment Details</CardTitle>
        <div className="ia-mgrid ia-mgrid--3">
          {PAYMENT.map(([l, v], i) => (
            <div key={i} className="ia-field"><span className="ia-field__label">{l}</span><span className="ia-field__value ia-field__value--md">{v}</span></div>
          ))}
        </div>
        <div className="ia-btn-wrapper">
        <button onClick={onRelease} disabled={released} className={`ia-btn-gradient ia-btn-gradient--lg${released ? " ia-btn-gradient--done" : ""}`}>
          {released && <Ic.circleCheck width={16} height={16} />}{released ? "Payment Released" : "Release Payment"}
        </button>
        </div>
      </Card>
    </div>
  );
}

// ============================== STEP 8 =======================================
type AuditEntry = { who: string; title: string; detail: string | null; time: string; link?: string };
function Step8({ entries }: { entries: AuditEntry[] }) {
  return (
    <Card>
      <div className="ia-card-header-row">
        <CardTitle>Timeline</CardTitle>
        <button className="ia-btn-outline"><Ic.download width={14} height={14} />Export report</button>
      </div>
      <div className="ia-timeline">
        {entries.map((e, i) => (
          <div key={i} className="ia-timeline-row">
            <div className="ia-timeline-rail">
              <span className={`ia-timeline-dot${e.who === "AI Engine" ? " ia-timeline-dot--ai" : ""}`} />
              {i < entries.length - 1 && <span className="ia-timeline-line" />}
            </div>
            <div className="ia-timeline-content">
              <div className="ia-timeline-head">
                <span className="ia-timeline-title">{e.who} — {e.title}</span>
                <span className="ia-timeline-time">{e.time}</span>
              </div>
              {e.detail && <span className="ia-timeline-detail">{e.detail}</span>}
              {e.link && <button className="ia-timeline-link">{e.link}</button>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ============================== STEP 9 =======================================
function BarRow({ label, value, max, variant }: { label: string; value: number; max: number; variant: "trend" | "funnel" }) {
  return (
    <div className={`ia-bar-row ia-bar-row--${variant}`}>
      <span className="ia-bar-row__label">{label}</span>
      <div className="ia-bar-track"><div className={`ia-bar-fill ia-bar-fill--${variant === "trend" ? "gradient" : "solid"}`} style={{ width: `${(value / max) * 100}%` }} /></div>
      <span className="ia-bar-row__value">{variant === "funnel" ? value.toLocaleString() : value}</span>
    </div>
  );
}
function Step9() {
  const maxTrend = Math.max(...EXC_TREND.map((t) => t[1]));
  const maxFun = FUNNEL[0][1];
  return (
    <div className="ia-stack">
      <div className="ia-kpi">
        {ANALYTICS_KPIS.map(([l, v], i) => (
          <div key={i} className="ia-kpi-card">
            <span className="ia-kpi-card__label">{l}</span>
            <span className="ia-kpi-card__value">{v}</span>
          </div>
        ))}
      </div>
      <div className="ia-mgrid ia-mgrid--2">
        <Card>
          <CardTitle>Exception Trend by Category</CardTitle>
          <div className="ia-bar-list">{EXC_TREND.map(([l, v], i) => <BarRow key={i} label={l as string} value={v as number} max={maxTrend} variant="trend" />)}</div>
        </Card>
        <Card>
          <CardTitle>Processing Funnel</CardTitle>
          <div className="ia-bar-list">{FUNNEL.map(([l, v], i) => <BarRow key={i} label={l as string} value={v as number} max={maxFun} variant="funnel" />)}</div>
        </Card>
      </div>
    </div>
  );
}

// ============================== VIEW =========================================
export default function InvoiceAudit() {
  const [vendor, setVendor] = useState("");
  const [txnSet, setTxnSet] = useState("");
  const [step, setStep] = useState(0);
  const [processed, setProcessed] = useState(false);
  const [approvalActions, setApprovalActions] = useState<string[]>([]);
  const [released, setReleased] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [aiVisible, setAiVisible] = useState(true);

  const loaded = Boolean(vendor && txnSet);

  const loadSet = (label: string) => { setTxnSet(label); setStep(1); };
  const takeAction = (a: string) => {
    setApprovalActions((prev) => (prev.includes(a) ? prev : [...prev, a]));
  };

  const auditEntries: AuditEntry[] = [
    ...AUDIT_BASE,
    ...approvalActions.map((a) => ({ who: "You", title: `Approval action: ${a}`, detail: null, time: "07:26 pm" })),
    ...(released ? [{ who: "You", title: "Payment released", detail: "INV-9114 · $6,901.20 released via Bank Transfer (ACH)", time: "07:28 pm" }] : []),
  ];

  const renderStep = () => {
    if (step === 0) return <Step1 vendor={vendor} onSelectVendor={setVendor} txnSet={txnSet} onLoadSet={loadSet} />;
    if (!loaded) return <EmptyState title="Load a transaction set" sub="Select a vendor and load a transaction set in Step 1 to populate this screen." />;
    switch (step) {
      case 1: return <Step2 />;
      case 2: return <Step3 processed={processed} onProcess={() => setProcessed(true)} />;
      case 3: return <EmptyState title="No exceptions detected — clean scenario" sub="All commercial, operational, compliance, and fraud/risk checks passed." />;
      case 4: return <EmptyState title="No open exceptions for this transaction set." />;
      case 5: return <Step6 onAction={takeAction} actionsTaken={approvalActions} />;
      case 6: return <Step7 onRelease={() => setReleased(true)} released={released} />;
      case 7: return <Step8 entries={auditEntries} />;
      case 8: return <Step9 />;
      default: return null;
    }
  };

  return (
    <div className="ia-page">
      <div className="ia-page-header">
        <span className="ia-eyebrow">Invoice &amp; Payments</span>
        <h1 className="ia-title">Invoice Audit (3-way match)</h1>
      </div>
      <StatusBar vendor={vendor} txnSet={txnSet} />
      <div className="ia-workspace">
        <div className="ia-steps">
          <StepsList active={step} onSelect={setStep} enabled={loaded} />
        </div>
        <div className="ia-step-content">{renderStep()}</div>
        {aiVisible ? (
          <div className="ia-ai">
            <AIPanel onHide={() => setAiVisible(false)} onPrompt={(q) => setAiInput(q)} input={aiInput} setInput={setAiInput} />
          </div>
        ) : (
          <button onClick={() => setAiVisible(true)} className="ia-show-ai-btn">
            <Ic.sparkles width={14} height={14} />Show AI Panel
          </button>
        )}
      </div>
    </div>
  );
}
